import { useMemo, useState } from 'react';
import { App, Button, Card, Checkbox, Drawer, Flex, Input, Tabs, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { coverageConfigQueryKeys, useCreateCoverageConfig, useGetAvailableCoverages } from '@/features/quotations/hooks/useCoverageConfigs';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';

const { Text } = Typography;

const FILTER_KEYS = { all: 'all', main: 'main', assistance: 'assistance' };

export const AddCoveragesDrawer = ({ open, onClose, planId, existingIds = [] }) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [filterKey, setFilterKey] = useState(FILTER_KEYS.all);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const availableCoveragesQuery = useGetAvailableCoverages({ enabled: open });
  const createConfigMutation = useCreateCoverageConfig();

  const availableCoverages = availableCoveragesQuery.data ?? [];
  const filteredCoverages = useMemo(() => {
    let list = availableCoverages.filter((c) => !existingIds.includes(c.id));
    if (filterKey === FILTER_KEYS.main) list = list.filter((c) => c.coverageType === 'main');
    if (filterKey === FILTER_KEYS.assistance) list = list.filter((c) => c.coverageType === 'assistance');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.code && c.code.toLowerCase().includes(q))
      );
    }
    return list;
  }, [availableCoverages, filterKey, search, existingIds]);

  const handleConfirm = async () => {
    if (!planId || selectedIds.length === 0) return;

    // Se obtiene los objetos de las coberturas seleccionadas
    const selectedCoverages = availableCoverages.filter((availableCoverage) => selectedIds.includes(availableCoverage.id));

    try {
      for (const selectedCoverage of selectedCoverages) {
        await createConfigMutation.mutateAsync({
          agreementPlanId: planId,
          coverageId: selectedCoverage.id,
        });
      }
      void queryClient.invalidateQueries({
        queryKey: coverageConfigQueryKeys.list({ planId, active: true }),
      });
      notification.success({
        message: <Text strong>Coberturas añadidas</Text>,
        description: '¡Las coberturas se han añadido al plan correctamente!',
      });
      setSelectedIds([]);
      setSearch('');
      setFilterKey(FILTER_KEYS.all);
      onClose?.();
    } catch (error) {
      notification.error({
        message: <Text strong>Coberturas no añadidas</Text>,
        description: getErrorMessage(error),
      });
    }
  };

  const isLoading = createConfigMutation.isPending;

  const handleClose = () => {
    setSelectedIds([]);
    setSearch('');
    setFilterKey(FILTER_KEYS.all);
    onClose?.();
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const filterTabs = [
    { key: FILTER_KEYS.all, label: 'Todas' },
    { key: FILTER_KEYS.main, label: 'Principales' },
    { key: FILTER_KEYS.assistance, label: 'Asistencias' },
  ];

  return (
    <Drawer
      title='Añadir coberturas'
      open={open}
      onClose={handleClose}
      width='32em'
      closable={{
        placement: 'end',
      }}
      destroyOnHidden
      footer={
        <Flex gap='small' justify='end'>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            type='primary'
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
            loading={isLoading}
          >
            Añadir coberturas
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap='middle'>
        <Text type='secondary'>
          Selecciona coberturas para agregarlas al plan.
        </Text>
        <Flex vertical>
          <Tabs
            activeKey={filterKey}
            onChange={setFilterKey}
            items={filterTabs}
            size='small'
          />
          <Flex vertical gap='large'>
            <Input
              placeholder='Buscar cobertura por nombre o palabra clave'
              suffix={<Text type='secondary'><SearchOutlined /></Text>}
              value={search}
              onChange={handleSearch}
              allowClear
            />
            {availableCoveragesQuery.isLoading ? (
              <LoadingIndicator />
            ) : availableCoveragesQuery.isError ? (
              <ErrorMessage error={availableCoveragesQuery.error} />
            ) : (
              <Flex vertical gap='small'>
                <Text type='secondary'>
                  {filteredCoverages.length} resultados
                </Text>
                <Card
                  size='small'
                  style={{
                    maxHeight: '70vh',
                    overflowY: 'auto',
                  }}
                >
                  {filteredCoverages.length === 0 ? (
                    <Text type='secondary'>No hay coberturas disponibles para agregar.</Text>
                  ) : (
                    <Checkbox.Group
                      value={selectedIds}
                      onChange={setSelectedIds}
                      style={{
                        width: '100%',
                      }}
                    >
                      <Flex vertical gap='small' style={{ width: '100%' }}>
                        {filteredCoverages.map((coverage) => (
                          <Card
                            key={coverage.id}
                            size='small'
                            style={{
                              backgroundColor: '#fafafa',
                            }}
                          >
                            <Checkbox value={coverage.id}>{coverage.name}</Checkbox>
                          </Card>
                        ))}
                      </Flex>
                    </Checkbox.Group>
                  )}
                </Card>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Drawer>
  );
};
